import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import { PrioSpinner, Select } from '@prio365/prio365-react-library';

import {
  getContactsIsFetching,
  getInternalProjectContactsIsFetching,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { Contact } from '../../../models/Contact';
import {
  ContactId,
  ProjectId,
  ContactType,
  OfficeId,
} from '../../../models/Types';
import { isTemporaryId } from '../../../util';
import { useTranslation } from 'react-i18next';
import { apiSearchContacts } from '../api';
import { fetchInternalProjectContacts } from '../../projects/actions';
import useContactsContext from '../hooks/useContactsProvider';
import useInternalProjectContactsContext from '../../projects/hooks/useInternalProjectContactsContext';
import { Size } from '@prio365/prio365-react-library/lib/Select/components/Select';
import { makePrioStyles } from '../../../theme/utils';
import { OptionType } from '@prio365/prio365-react-library/lib/Select/components/Option';
import useOfficesContext from '../../companies/hooks/useOfficesContext';

const useStyles = makePrioStyles((theme) => ({
  dropdown: {
    '& .prio-select-item-option-content': {
      height: 'fit-content',
    },
  },
  // Scoped override: the global single-select rule clamps the selected value to a
  // single 28px line (line-height: 28px; white-space: nowrap), which squashes the
  // two-line contact label (name + office) in the closed field. Here we let it show
  // two lines, vertically centered in the control, matching the dropdown options.
  // Assumes size="default" (40px) — two lines do not fit the 32px "small" control.
  select: {
    '& .prio-select-selection-item': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%',
      lineHeight: 1.2,
      whiteSpace: 'normal',
    },
  },
}));
const useContactPicker = (props: {
  projectId?: ProjectId;
  officeId?: OfficeId;
  value?: string | string[];
}) => {
  const { projectId, officeId, value } = props;

  const { contacts } = useContactsContext(true);
  const { getInternalProjectContactsContacts } =
    useInternalProjectContactsContext();

  return useMemo(() => {
    if (projectId) {
      return getInternalProjectContactsContacts(projectId);
    }

    if (officeId) {
      const valueIds: string[] = Array.isArray(value)
        ? value
        : value
          ? [value]
          : [];

      const buContacts = contacts.filter(
        (c) => c.officeId === officeId && c.contactType === 'InternalContact'
      );

      const valueContacts = contacts.filter(
        (c) =>
          valueIds.includes(c.contactId) &&
          !buContacts.some((bu) => bu.contactId === c.contactId)
      );

      return [...buContacts, ...valueContacts];
    }
    return contacts;
  }, [
    contacts,
    getInternalProjectContactsContacts,
    officeId,
    projectId,
    value,
  ]);
};

interface ContactPickerPrioCLProps {
  value?: string | string[];
  label?: string;
  onChange?: (value: string | string[], companyId?: string) => void;
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  excludedContactIds?: ContactId[];
  onlyInternalProject?: boolean;
  contactType?: ContactType;
  projectId?: ProjectId;
  officeId?: OfficeId;
  multiple?: boolean;
  fetch?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  filter?: (contact: Contact) => boolean;
  size?: Size;
  hideShowMoreButton?: boolean;
  hideSubLabel?: boolean;
}

export const ContactPickerPrioCL: React.FC<ContactPickerPrioCLProps> =
  React.memo((props) => {
    //#region ------------------------------ Defaults
    const {
      className,
      style,
      value,
      label,
      onChange,
      onClick,
      disabled,
      excludedContactIds,
      onlyInternalProject: onlyInternal,
      contactType,
      projectId,
      officeId,
      multiple,
      loading,
      filter,
      allowClear,
      size = 'small',
      hideShowMoreButton = false,
      hideSubLabel = false,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const classes = useStyles();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const _projectId = useMemo(() => {
      return onlyInternal ? projectId : undefined;
    }, [onlyInternal, projectId]);

    const _officeId = useMemo(() => {
      return contactType === 'InternalContact' ? officeId : undefined;
    }, [contactType, officeId]);

    const allContacts = useContactPicker({
      projectId: _projectId,
      officeId: _officeId,
      value,
    });

    const { getOfficeById } = useOfficesContext();

    const [onlineData, setOnlineData] = useState<Contact[]>([]);
    const [onlineLoading, setOnlineLoading] = useState<boolean>(false);

    const isFetching = useSelector<RootReducerState, boolean>((state) =>
      onlyInternal
        ? getInternalProjectContactsIsFetching(state)
        : getContactsIsFetching(state)
    );

    const contacts: Contact[] = allContacts;
    const additionalContacts: Contact[] = onlineData;

    const getFilteredContactOptions = useCallback(
      (
        contacts: Contact[],
        excludedContactIds?: string[],
        filter?: (contact: Contact) => boolean
      ): OptionType[] => {
        return contacts
          .filter(
            (c) =>
              c.isArchived === false &&
              (!contactType || c.contactType === contactType) &&
              !isTemporaryId(c.contactId) &&
              !excludedContactIds?.includes(c.contactId) &&
              (!filter || filter(c))
          )
          .map((contact: Contact) => ({
            value: contact.contactId,
            searchValue: `${contact?.firstName} ${contact?.lastName}`,
            title: `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`,
            label: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontSize: 'var(--prioCl-font-size-extra-small)',
                  lineHeight: 1.2,
                }}
              >
                <span
                  style={{ lineHeight: '16px' }}
                >{`${contact.firstName} ${contact.lastName}`}</span>
                {!hideSubLabel && (
                  <div className="prio-subLabel">
                    <span>{getOfficeById(contact.officeId)?.name}</span>
                  </div>
                )}
              </div>
            ),
          }));
      },
      [contactType, getOfficeById, hideSubLabel]
    );

    const options = React.useMemo(
      () => getFilteredContactOptions(contacts, excludedContactIds, filter),
      [contacts, excludedContactIds, filter, getFilteredContactOptions]
    );
    const additionalOptions = React.useMemo(() => {
      if (hideShowMoreButton) return [];

      const _additionalOptions = getFilteredContactOptions(
        additionalContacts,
        excludedContactIds,
        filter
      );
      const _additionalOptionsWithoutOptions = _additionalOptions.filter(
        (option) => {
          return !options.some((o) => o.value === option.value);
        }
      );

      const hasOptions = _additionalOptionsWithoutOptions.length > 0;

      // show show more button even when no additional options are available to trigger online search
      return hasOptions ? _additionalOptionsWithoutOptions : [{}];
    }, [
      options,
      additionalContacts,
      excludedContactIds,
      hideShowMoreButton,
      filter,
      getFilteredContactOptions,
    ]);
    //#region ------------------------------ Methods / Handlers
    const handleOnChange: (value: string) => void = onChange
      ? (value: string) => {
          if (contactType === 'ExternalContact') {
            onChange(
              value ?? '',
              options.find((contact) => contact.contactId === value).companyId
            );
          } else {
            onChange(value ?? '');
          }
        }
      : null;

    const searchOnline = async () => {
      setOnlineLoading(true);
      const { result, data } = await apiSearchContacts('', contactType);
      if (result.status >= 200 && result.status < 300) {
        setOnlineData(data);
      } else {
        notification.open({
          message: t('common:error'),
          description: t('contacts:errorMessages.fetchError'),
        });
      }
      setOnlineLoading(false);
    };
    //#endregion

    //#region ------------------------------ Effects
    useEffect(() => {
      if (projectId && onlyInternal) {
        dispatch(fetchInternalProjectContacts(projectId));
      }
    }, [projectId, onlyInternal, dispatch]);
    //#endregion

    return (
      <Select
        className={[classes.select, className].filter(Boolean).join(' ')}
        dropdownClassName={classes.dropdown}
        style={{
          ...(style ?? {}),
          ...(disabled || isFetching
            ? { backgroundColor: 'rgb(0,0,0,0.05)' }
            : {}),
        }}
        mode={multiple ? 'multiple' : null}
        size={size}
        showSearch
        value={value ?? (multiple ? [] : value)}
        placeholder={label}
        onChange={handleOnChange}
        onClick={onClick}
        filterOption={(input, option) => {
          return (
            option.searchValue
              ?.toString()
              ?.toLowerCase()
              ?.indexOf(input.toLowerCase()) >= 0
          );
        }}
        disabled={disabled || isFetching}
        loading={loading || isFetching}
        allowClear={allowClear}
        options={options}
        additionalOptions={additionalOptions}
        onShowAdditionalOptionsClick={searchOnline}
        withSubLabel={!multiple}
        dropdownRender={(menu) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {onlineLoading && (
              <div
                style={{
                  background: '#FFFFFFBF',
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1,
                }}
              >
                <PrioSpinner size="small" />
              </div>
            )}
            {menu}
          </div>
        )}
      />
    );
  });

export default ContactPickerPrioCL;
